import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup } from '@vibesites/runtime/react';
import { RichTextContent, EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

// ─── Slot group definitions ───────────────────────────────────────────────────
//
// The collage renders four photo tiles in a staggered two-column layout. Each
// tile is its own slot group bound to `photo_gallery` so the field generator
// can select a distinct, neighborhood-relevant photo per tile (each generator
// selection is frozen as a VALUE override, keeping alt text matched to the
// chosen image via `sourceBinding`).

const photo1Def: SlotGroupDefinition = {
  label: 'Collage Photo 1',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Select a photo that captures the surrounding neighborhood or local area — nearby parks, waterfront, recreation, landmarks, or streetscapes.',
    context:
      'One of four photos in the neighborhood collage on the home page. Each tile must use a different photo.',
    sourceCollection: 'photo_gallery',
  },
};

const photo2Def: SlotGroupDefinition = {
  label: 'Collage Photo 2',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[1].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[1].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 1,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Select a photo that captures the surrounding neighborhood or local area — nearby parks, waterfront, recreation, landmarks, or streetscapes.',
    context:
      'One of four photos in the neighborhood collage on the home page. Each tile must use a different photo.',
    sourceCollection: 'photo_gallery',
  },
};

const photo3Def: SlotGroupDefinition = {
  label: 'Collage Photo 3',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[2].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[2].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 2,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Select a photo that captures the surrounding neighborhood or local area — nearby parks, waterfront, recreation, landmarks, or streetscapes.',
    context:
      'One of four photos in the neighborhood collage on the home page. Each tile must use a different photo.',
    sourceCollection: 'photo_gallery',
  },
};

const photo4Def: SlotGroupDefinition = {
  label: 'Collage Photo 4',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[3].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[3].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 3,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Select a photo that captures the surrounding neighborhood or local area — nearby parks, waterfront, recreation, landmarks, or streetscapes.',
    context:
      'One of four photos in the neighborhood collage on the home page. Each tile must use a different photo.',
    sourceCollection: 'photo_gallery',
  },
};

const ctaDef: SlotGroupDefinition = {
  label: 'Neighborhood CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/neighborhood' },
    label: { label: 'Link Text', type: 'text', defaultValue: "See What's Nearby" },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ─── Component ────────────────────────────────────────────────────────────────

const COLLAGE_SIZES = '(min-width: 768px) 25vw, 50vw';

export function NeighborhoodShowcase() {
  const photo1 = useSlotGroup('neighborhoodshowcase.photo1', photo1Def);
  const photo2 = useSlotGroup('neighborhoodshowcase.photo2', photo2Def);
  const photo3 = useSlotGroup('neighborhoodshowcase.photo3', photo3Def);
  const photo4 = useSlotGroup('neighborhoodshowcase.photo4', photo4Def);
  const cta = useSlotGroup('neighborhoodshowcase.cta', ctaDef);

  useRevealStyles();
  const imageRef = useScrollReveal<HTMLDivElement>();
  const titleRef = useScrollReveal<HTMLDivElement>();
  const contentRef = useScrollReveal<HTMLDivElement>();

  return (
    <section className="pt-12 md:pt-20 pb-12 md:pb-20 bg-[var(--vibe-color-background)]">
      <div className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
          {/* Left column — photo collage */}
          <div ref={imageRef} className="reveal-slide-right order-2 md:order-1">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-4">
                <EditableSlotGroup groupPath="neighborhoodshowcase.photo1" definition={photo1Def}>
                  <div className="relative w-full aspect-[4/3] overflow-hidden rounded-md shadow-md">
                    {photo1.image ? (
                      <VibesitesImage
                        url={photo1.image}
                        alt={photo1.alt || ''}
                        sizes={COLLAGE_SIZES}
                        className="absolute inset-0 w-full h-full object-cover object-center"
                      />
                    ) : (
                      <div className="absolute inset-0 bg-[var(--vibe-color-surface)]" />
                    )}
                  </div>
                </EditableSlotGroup>
                <EditableSlotGroup groupPath="neighborhoodshowcase.photo2" definition={photo2Def}>
                  <div className="relative w-full aspect-[3/4] overflow-hidden rounded-md shadow-md">
                    {photo2.image ? (
                      <VibesitesImage
                        url={photo2.image}
                        alt={photo2.alt || ''}
                        sizes={COLLAGE_SIZES}
                        className="absolute inset-0 w-full h-full object-cover object-center"
                      />
                    ) : (
                      <div className="absolute inset-0 bg-[var(--vibe-color-surface)]" />
                    )}
                  </div>
                </EditableSlotGroup>
              </div>
              <div className="flex flex-col gap-4">
                <EditableSlotGroup groupPath="neighborhoodshowcase.photo3" definition={photo3Def}>
                  <div className="relative w-full aspect-[3/4] overflow-hidden rounded-md shadow-md">
                    {photo3.image ? (
                      <VibesitesImage
                        url={photo3.image}
                        alt={photo3.alt || ''}
                        sizes={COLLAGE_SIZES}
                        className="absolute inset-0 w-full h-full object-cover object-center"
                      />
                    ) : (
                      <div className="absolute inset-0 bg-[var(--vibe-color-surface)]" />
                    )}
                  </div>
                </EditableSlotGroup>
                <EditableSlotGroup groupPath="neighborhoodshowcase.photo4" definition={photo4Def}>
                  <div className="relative w-full aspect-[4/3] overflow-hidden rounded-md shadow-md">
                    {photo4.image ? (
                      <VibesitesImage
                        url={photo4.image}
                        alt={photo4.alt || ''}
                        sizes={COLLAGE_SIZES}
                        className="absolute inset-0 w-full h-full object-cover object-center"
                      />
                    ) : (
                      <div className="absolute inset-0 bg-[var(--vibe-color-surface)]" />
                    )}
                  </div>
                </EditableSlotGroup>
              </div>
            </div>
          </div>

          {/* Right column — copy */}
          <div className="order-1 md:order-2 text-center md:text-left">
            {/* Heading block — zooms from back to front */}
            <div ref={titleRef} className="reveal-zoom-front">
              <RichTextContent
                path="neighborhoodshowcase.pre_title"
                label="Subtitle"
                defaultValue="<p>Experiences Outside Your Doorstep</p>"
                as="p"
                className="font-body text-sm sm:text-base font-semibold text-[var(--vibe-color-primary)] mb-5 tracking-[0.3em] uppercase"
              />

              <RichTextContent
                path="neighborhoodshowcase.title"
                label="Title"
                defaultValue='<p>Stylish Apartments In <span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span></p>'
                as="h2"
                className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-[var(--vibe-color-text)] mb-8 leading-[1.05]"
              />
            </div>

            {/* Blurb + CTA — fade up from the bottom */}
            <div ref={contentRef} className="reveal-fade-up">
              <RichTextContent
                path="neighborhoodshowcase.description"
                label="Description"
                defaultValue="<p>Your neighborhood puts parks, shopping, dining, and entertainment just minutes from your front door. Explore everything that makes this location the perfect place to call home.</p>"
                as="div"
                className="font-body text-base sm:text-lg text-[var(--vibe-color-text-muted)] max-w-xl mx-auto md:mx-0 leading-relaxed [&_a]:text-primary [&_a]:underline"
              />

              <EditableSlotGroup groupPath="neighborhoodshowcase.cta" definition={ctaDef}>
                <a
                  href={cta.url || '/neighborhood'}
                  target={cta._target || '_self'}
                  className="inline-block text-center mt-9 rounded-full px-10 py-4 text-base font-normal normal-case bg-secondary text-secondary-foreground hover:brightness-90 transition-[filter,colors]"
                >
                  {cta.label || "See What's Nearby"}
                </a>
              </EditableSlotGroup>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Content Schema ───────────────────────────────────────────────────────────

export const contentSchema: ComponentContentSchema = {
  slot: 'NeighborhoodShowcase',
  displayName: 'Neighborhood Showcase',
  tokens: {
    'neighborhoodshowcase.pre_title': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: '<p>Experiences Outside Your Doorstep</p>',
    },
    'neighborhoodshowcase.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue:
        '<p>Stylish Apartments In <span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span></p>',
    },
    'neighborhoodshowcase.description': {
      label: 'Description',
      type: 'richtext',
      defaultField: 'property_neighborhood_blurb',
      defaultValue:
        '<p>Your neighborhood puts parks, shopping, dining, and entertainment just minutes from your front door. Explore everything that makes this location the perfect place to call home.</p>',
    },
  },
  slotGroups: {
    'neighborhoodshowcase.photo1': photo1Def,
    'neighborhoodshowcase.photo2': photo2Def,
    'neighborhoodshowcase.photo3': photo3Def,
    'neighborhoodshowcase.photo4': photo4Def,
    'neighborhoodshowcase.cta': ctaDef,
  },
};

(NeighborhoodShowcase as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default NeighborhoodShowcase;
